/**
 * OpsHub — central landing for anyone on one or more ops teams.
 *
 * Mounted at /ops. Calls /api/ops/my-systems to figure out which of the
 * five operational portals (POS / Security / Dining / CCTV / Housing)
 * the current admin can enter. Tiles deep-link straight into each portal.
 *
 * Super admins see all 5 systems with the platform-wide team size.
 * Regular admins see only the systems they're a member of, with their
 * specific role.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ShoppingCart, Shield, UtensilsCrossed, Video, Home, Users,
  ArrowLeft, ArrowRight, Loader2, Sparkles,
} from 'lucide-react';
import api from '@/lib/api';

const META = {
  pos:      { icon: ShoppingCart,    accent: 'emerald', gradient: 'from-emerald-600 to-teal-600',  ring: 'ring-emerald-100' },
  security: { icon: Shield,          accent: 'red',     gradient: 'from-red-600 to-rose-600',      ring: 'ring-red-100' },
  dining:   { icon: UtensilsCrossed, accent: 'amber',   gradient: 'from-amber-500 to-orange-500',  ring: 'ring-amber-100' },
  cctv:     { icon: Video,           accent: 'indigo',  gradient: 'from-indigo-600 to-blue-600',   ring: 'ring-indigo-100' },
  housing:  { icon: Home,            accent: 'teal',    gradient: 'from-teal-600 to-emerald-600',  ring: 'ring-teal-100' },
};

const OpsHub = () => {
  const navigate = useNavigate();
  const { data: systems = [], isLoading, isError } = useQuery({
    queryKey: ['ops-my-systems'],
    queryFn: () => api.get('/ops/my-systems').then(r => r.data),
    retry: false,
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ops-hub">
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-400 hover:text-slate-700"><ArrowLeft className="h-5 w-5" /></Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-teal-600" />
                Operations Hub
              </h1>
              <p className="text-xs text-slate-500">Every operational portal you're a member of — one click away.</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/teams')} data-testid="ops-hub-team-mgmt">
            <Users className="h-3.5 w-3.5 mr-1" />Team Management
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading your systems…</div>
        ) : isError ? (
          <Card><CardContent className="py-16 text-center text-slate-500 text-sm">
            Could not load your ops systems. Check that you're signed in.
          </CardContent></Card>
        ) : systems.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-slate-500 text-sm">
            <Shield className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-medium text-slate-700">No ops portals yet</p>
            <p className="mt-1">Ask a super admin or the relevant manager to add you to a team.</p>
            <Button className="mt-4" variant="outline" onClick={() => navigate('/admin')}>Back to dashboard</Button>
          </CardContent></Card>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-5">
              {systems.length === 1
                ? `You belong to 1 operational portal.`
                : `You belong to ${systems.length} operational portal${systems.length === 1 ? '' : 's'}.`}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {systems.map(sys => {
                const meta = META[sys.system] || META.pos;
                const Icon = meta.icon;
                return (
                  <Card
                    key={sys.system}
                    className={`overflow-hidden hover:shadow-lg transition-shadow ring-1 ${meta.ring}`}
                    data-testid={`ops-hub-card-${sys.system}`}
                  >
                    <div className={`bg-gradient-to-br ${meta.gradient} text-white p-5`}>
                      <div className="flex items-center justify-between mb-3">
                        <Icon className="h-7 w-7" />
                        {sys.is_super && <Badge variant="outline" className="bg-white/10 text-white border-white/30 text-[10px]">Super Admin</Badge>}
                      </div>
                      <p className="text-xs uppercase tracking-[0.2em] opacity-80">{sys.entity}</p>
                      <h2 className="text-xl font-bold mt-1">{sys.label}</h2>
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                        <span>Your role</span>
                        <Badge variant="outline" className="capitalize text-[10px]">{(sys.role || '').replace(/_/g, ' ')}</Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-4">
                        <span>Team size</span>
                        <span className="font-semibold text-slate-800">{sys.team_size}</span>
                      </div>
                      <Button
                        className="w-full"
                        onClick={() => navigate(`/${sys.system}`)}
                        data-testid={`ops-hub-open-${sys.system}`}
                      >
                        Open {sys.label} <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default OpsHub;
